import React from 'react';

import { Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import ColoredButton from 'components/ColoredButton';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { ActionLibraryInstance } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';

type Props = {
  open: boolean;
  onClose: () => void;
  instance: ActionLibraryInstance;
  onSuccess: (updatedInstance: ActionLibraryInstance) => void;
};

type ErrorResponseData = {
  detail?: string | string[];
  delegation_comment?: string[];
};

const getErrorMessage = (data: unknown, fallbackMessage: string): string => {
  if (!data || typeof data !== 'object') return fallbackMessage;
  const d = data as ErrorResponseData;
  if (typeof d.detail === 'string' && d.detail.trim()) return d.detail;
  if (Array.isArray(d.detail) && d.detail[0]) return String(d.detail[0]);
  const fieldErr = d.delegation_comment?.[0];
  if (fieldErr) return fieldErr;
  return fallbackMessage;
};

const TakeBackInstanceDialog: React.FC<Props> = ({ open, onClose, instance, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [comment, setComment] = React.useState('');

  const takeBackMutation = useMutation(
    (delegationComment: string) =>
      actionLibraryInstancesApi.delegateActionLibraryInstance(instance.id, {
        delegatedTo: null,
        delegationComment,
      }),
    {
      onSuccess: result => {
        const updatedInstance = result.data;
        queryClient.invalidateQueries('ACTION_LIBRARY_INSTANCES_GET_ALL', { refetchInactive: true });
        queryClient.invalidateQueries(['ACTION_LIBRARY_INSTANCE_STATUS_LOGS', instance.id], { refetchInactive: true });
        enqueueSnackbar(t(action_library_messages.success.instance_updated), { variant: 'success' });
        setComment('');
        onSuccess(updatedInstance);
        onClose();
      },
      onError: (err: unknown) => {
        const axiosErr = err as { response?: { status?: number; data?: unknown } };
        const status = axiosErr?.response?.status;
        const data = axiosErr?.response?.data;
        if (status === 403) {
          enqueueSnackbar(t(action_library_messages.errors.delegate_forbidden), { variant: 'error' });
          return;
        }
        enqueueSnackbar(getErrorMessage(data, t(action_library_messages.errors.delegate_failed)), { variant: 'error' });
      },
    },
  );

  const handleClose = () => {
    setComment('');
    onClose();
  };

  const handleSubmit = () => {
    takeBackMutation.mutate(comment.trim());
  };

  const commentTrimmed = comment.trim();
  const canSubmit = commentTrimmed.length > 0;

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle>{t(action_library_messages.instance_actions.take_back_dialog_title)}</DialogTitle>
      <DialogContent>
        <TextField
          error={commentTrimmed.length === 0 && comment.length > 0}
          fullWidth
          helperText={
            commentTrimmed.length === 0 && comment.length > 0
              ? t(action_library_messages.instance_actions.take_back_comment_required)
              : undefined
          }
          label={t(action_library_messages.instance_actions.take_back_comment_placeholder)}
          multiline
          onChange={e => setComment(e.target.value)}
          required
          rows={3}
          value={comment}
          variant='outlined'
        />
      </DialogContent>
      <DialogActions>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={false}
          icon={false}
          onClick={handleClose}
          showLoader={false}
          textLike={false}
          variant='outlined'
        >
          {t(general_messages.cancel[0], general_messages.cancel[1])}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={!canSubmit || takeBackMutation.isLoading}
          icon={false}
          onClick={handleSubmit}
          showLoader={takeBackMutation.isLoading}
          textLike={false}
          variant='contained'
        >
          {t(action_library_messages.instance_actions.take_back)}
        </ColoredButton>
      </DialogActions>
    </Dialog>
  );
};

export default TakeBackInstanceDialog;
